import type {ReactElement} from 'react';
import type {PosterObject} from '@PosterWhiteboard/poster/poster.types';
import React, {useEffect, useState} from 'react';
import {Dropdown} from '@Components/dropdown-v2';
import {IconType} from '@Components/icon-v2/icon.types';
import {Type} from '@Components/button';
import {NavRightSideOptionTypes} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-right-options/nav-right-options.types';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import styles from './nav-download-option.module.scss';
import {DownloadPanel} from '@Components/download-panel/download-panel';
import BaseNavOption from '@Components/poster-editor/components/poster-editor-nav-bar/components/base-nav-option/base-nav-option';
import {openNewDownloadModal} from '@Modals/download-modal/download-modal.library';
import {DownloadStateTypes, setCurrentViewingOrderIdKey, setIsViewingDropboxDownloads, updateDownloadPanelOpenState} from '@Components/download-panel/download-panel.reducer';
import {updateWebDownloadPanelOpenState} from '@Components/poster-editor/poster-editor-reducer';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {isUserLoggedIn} from '@Libraries/user.library';
import {isEditorMobileVariant} from '@Components/poster-editor/library/is-editor-mobile-variant';

interface NavDownloadOptionProps {
  downloadOptionType?: NavRightSideOptionTypes;
}

export function NavDownloadOption({downloadOptionType = NavRightSideOptionTypes.BUTTON}: NavDownloadOptionProps): ReactElement {
  const isInitialized = useAppSelector((state) => {
    return state.posterEditor.posterObject?.isInitialized;
  });
  const hasPosterObject = useAppSelector((state) => {
    return !!state.posterEditor.posterObject;
  });
  const orderStateHashmapNew = useAppSelector((state) => {
    return state.downloadDialog.orderStateHashmapNew;
  });
  const currentlyProcessingOrderIdKeys = useAppSelector((state) => {
    return state.downloadDialog.currentlyProcessingOrderIdKeys;
  });
  const isFileDropdownOpen = useAppSelector((state) => {
    return state.downloadDialog.isInnerDropdownOpen;
  });
  const isSliderBeingDragged = useAppSelector((state) => {
    return state.downloadDialog.isSliderBeingDragged;
  });
  const isExternalModalOpen = useAppSelector((state) => {
    return state.downloadDialog.isExternalModalOpen;
  });
  const triggerOpenDownloadWebPanel = useAppSelector((state) => {
    return state.posterEditor.triggerOpenDownloadWebPanel;
  });
  const dispatch = useAppDispatch();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [savedPosterObject, setSavedPosterObject] = useState<PosterObject | null>(null);

  useEffect(() => {
    if (triggerOpenDownloadWebPanel) {
      setSavedPosterObject(window.posterEditor?.whiteboard?.toObject() ?? null);
      if (isUserLoggedIn()) {
        void window.posterEditor?.whiteboard?.savePoster.save();
      }
      dispatch(setCurrentViewingOrderIdKey(undefined));
      dispatch(setIsViewingDropboxDownloads(false));
      dispatch(updateDownloadPanelOpenState(true));
      setIsDropdownOpen(true);
      dispatch(updateWebDownloadPanelOpenState(false));
    }
  }, [triggerOpenDownloadWebPanel]);

  if (!hasPosterObject) {
    throw new Error('posterObject is not defined');
  }

  const allOrders = currentlyProcessingOrderIdKeys.map((key) => orderStateHashmapNew[key]).filter((order) => order !== undefined);
  const downloadingOrders = allOrders.filter((order) => order.state === DownloadStateTypes.DOWNLOADING);
  const completedOrders = allOrders.filter((order) => order.state === DownloadStateTypes.READY);
  const totalOrders = allOrders.length;

  const isDownloading = (): boolean => {
    return downloadingOrders.length > 0;
  };

  const getAverageProgress = (): number => {
    if (downloadingOrders.length === 0) {
      return 0;
    }
    const totalProgress = downloadingOrders.reduce((sum, order) => sum + (order.displayProgress ?? 0), 0);
    return totalProgress / downloadingOrders.length;
  };

  const getProgress = (): ReactElement | null => {
    if (!isDownloading()) {
      return null;
    }
    return (
      <div
        style={{
          width: `${Math.round(getAverageProgress())}%`,
        }}
        className={styles.progress}
      />
    );
  };

  const getButtonText = (): string => {
    if (!isDownloading()) {
      return window.i18next.t('pmwjs_download');
    }

    if (totalOrders === 1) {
      return `${Math.round(getAverageProgress())}%`;
    }

    return `${completedOrders.length}/${totalOrders}`;
  };

  const preOpenDownloadPanel = async (): Promise<void> => {
    if (isUserLoggedIn()) {
      await window.posterEditor?.whiteboard?.savePoster.save();
    }
    dispatch(setCurrentViewingOrderIdKey(undefined));
    dispatch(setIsViewingDropboxDownloads(false));
  };

  const onClickDownloadTrigger = (): void => {
    trackPosterBuilderGA4Events(GA4EventName.NEW_DOWNLOAD_DIALOG_OPEN, {
      [GA4EventParamName.SOURCE]: 'editor',
    });

    void preOpenDownloadPanel();

    if (isEditorMobileVariant()) {
      const currentPosterObject = window.posterEditor?.whiteboard?.toObject();
      if (!currentPosterObject) {
        return;
      }
      openNewDownloadModal({
        id: 'mobile-download-panel',
        posterObject: currentPosterObject,
        isOpenedInEditor: true,
        isOpenedInModal: true,
        isOpenedInPrintShop: false,
      });
      return;
    } else {
      dispatch(updateDownloadPanelOpenState(true));
      setIsDropdownOpen(true);
    }
  };

  const getDownloadSelector = (): ReactElement => {
    return (
      <div>
        <BaseNavOption
          className={isDownloading() && !isEditorMobileVariant() ? styles.width : ''}
          buttonChildren={getProgress()}
          id="nav-download-option"
          icon="icon-cloud-download"
          text={getButtonText()}
          optionType={downloadOptionType}
          iconType={IconType.DARK_SECONDARY}
          buttonType={Type.DARK_SECONDARY}
          hasMarginRight
          isLoading={!isInitialized}
          onClickHandler={(): void => {
            onClickDownloadTrigger();
          }}
        />
      </div>
    );
  };

  if (isEditorMobileVariant()) {
    return getDownloadSelector();
  }

  const getPopup = (): ReactElement => {
    if (!savedPosterObject) {
      return <></>;
    }
    return <DownloadPanel id="editor-download-panel" posterObject={savedPosterObject} />;
  };

  const onDropdownOpen = (): void => {
    setSavedPosterObject(window.posterEditor?.whiteboard?.toObject() ?? null);
    onClickDownloadTrigger();
  };

  const onDropdownClose = (): void => {
    setIsDropdownOpen(false);
    setSavedPosterObject(null);
    dispatch(updateDownloadPanelOpenState(false));
  };

  return (
    <Dropdown
      open={isDropdownOpen}
      isControlled
      onOpen={onDropdownOpen}
      onClose={onDropdownClose}
      popUpHasCustomWidth
      maxHeight={530}
      popupSpacingClass="spacing-p-0"
      topOffset={-5}
      leftOffset={-10}
      doCloseOnInnerClick={false}
      position={DROPDOWN_POSITION.FIXED}
      popupClassName={`${styles.popup} _overflow-hidden`}
      selector={getDownloadSelector()}
      popup={getPopup()}
      alignment={ALIGNMENT_TYPE.BOTTOM_END}
      isResumable
      doesCloseOnOuterClick={!isFileDropdownOpen && !isSliderBeingDragged && !isExternalModalOpen}
    />
  );
}
